'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import type { Route } from 'next'
import { usePathname } from 'next/navigation'
import { ChevronDown, Menu, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, ButtonLink, IconButton, cn } from '@kiao-voice/ui-kit'
import { useAuthModal } from '../../features/auth/auth-modal-provider'
import { onAuthTokenChange, readStoredToken } from '../../features/auth/auth-storage'
import { trackSiteLinkClicked, type AnalyticsLocation } from '../../lib/analytics'
import { BrandLogo } from '../brand-logo'
import {
  NAV_ITEMS,
  PRODUCT_ITEMS,
  currentNavKey,
  isProductPath,
  type NavItem,
  type ProductNavItem,
} from './site-nav'

/** 滑鼠裝置才有 hover 展開；觸控裝置只認點擊。 */
function isHoverMode() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

/** 滑鼠從按鈕移到彈出層的途中不要掉出去。 */
const HOVER_CLOSE_DELAY_MS = 160

/**
 * 漢堡與 ✕ 共用的疊格與轉場。轉場掛在外層 `<span>` 而不是 `<svg>` 本身——
 * 多數瀏覽器不會對 SVG 元素上的旋轉、縮放與 opacity 動畫做硬體加速。
 */
const BURGER_ICON_CLASS =
  'grid [grid-area:1/1] [transition:opacity_.18s_ease,rotate_.24s_cubic-bezier(.2,.7,.2,1),scale_.24s_cubic-bezier(.2,.7,.2,1)] motion-reduce:[transition:none]'

/**
 * 全站公開頁的頁首。主導覽是「產品 ▾ · 方案 · 部落格」，三個產品頁收進下拉
 * 選單；訪客右側只有一顆登入／註冊按鈕，登入後則顯示帳號入口。
 *
 * 「產品」是 disclosure 不是 `role="menu"`：彈出層裡是三個一般連結，menu 語意
 * 會讓螢幕閱讀器承諾方向鍵巡覽與 `menuitem` 行為，而我們沒有實作那一套。
 *
 * 登入狀態刻意只讀 localStorage，不打 `/auth/me`：行銷頁是 LCP 關鍵路徑，而
 * 過期 token 最差的結果只是顯示「帳號」而不是「登入」——按下去會到 /app/account，
 * 那一頁自己會擋。
 */
export function SiteHeader() {
  const { t } = useTranslation('web')
  const { open } = useAuthModal()
  const pathname = usePathname()
  const [authed, setAuthed] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const headerRef = useRef<HTMLElement>(null)
  const burgerRef = useRef<HTMLButtonElement>(null)
  const productsWrapRef = useRef<HTMLDivElement>(null)
  const productsBtnRef = useRef<HTMLButtonElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const current = currentNavKey(pathname)
  const onProduct = isProductPath(pathname)

  // 首次渲染（含 SSR）一律是訪客狀態，掛載後讀 localStorage 修正，並追蹤 token
  // 變化，讓 modal 登入後立刻翻成已登入。
  useEffect(() => {
    const resolve = () => setAuthed(Boolean(readStoredToken()))
    resolve()
    return onAuthTokenChange(resolve)
  }, [])

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const openProducts = useCallback(() => {
    clearCloseTimer()
    setProductsOpen(true)
  }, [clearCloseTimer])

  const closeProducts = useCallback(() => {
    clearCloseTimer()
    setProductsOpen(false)
  }, [clearCloseTimer])

  useEffect(() => clearCloseTimer, [clearCloseTimer])

  // 頁首掛在常駐 layout 上，跨頁導覽不會重掛。換頁時關掉兩個選單，
  // 否則開著的選單會蓋住下一頁。
  useEffect(() => {
    setMenuOpen(false)
    setProductsOpen(false)
  }, [pathname])

  // 點外面與 Escape 關閉。用 `pointerdown` 而不是 `mousedown`：這個選單存在的
  // 理由就是 ≤720px 的觸控裝置。Escape 之後把焦點還給開關，鍵盤使用者才不會
  // 被留在已經隱藏的面板上。
  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) closeMenu()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu()
        burgerRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen, closeMenu])

  // 產品選單同一套規則，範圍縮到觸發鈕＋彈出層那一塊。
  useEffect(() => {
    if (!productsOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!productsWrapRef.current?.contains(event.target as Node)) closeProducts()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeProducts()
        productsBtnRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [productsOpen, closeProducts])

  const navLinkClass = (isCurrent: boolean, extra: string) =>
    cn(
      'border-b border-transparent py-1 whitespace-nowrap transition-colors',
      isCurrent
        ? 'border-foreground text-foreground'
        : 'text-muted hover:text-foreground hover:border-foreground',
      'focus-visible:rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-foreground',
      extra
    )

  const navLink = (item: NavItem, location: AnalyticsLocation, extra: string) => {
    const label = t(item.labelKey)
    const props = {
      className: navLinkClass(current === item.key, extra),
      onClick: () => {
        trackSiteLinkClicked(location, item.href)
        closeMenu()
      },
      ...(current === item.key ? { 'aria-current': 'page' as const } : {}),
    }
    // 未上線的 route 用 <a>：typed routes 認不得它，斷言蓋過去等於把
    // 「這個頁面存在」寫成假前提。
    return item.shipped ? (
      <Link key={item.key} href={item.href as Route} {...props}>
        {label}
      </Link>
    ) : (
      <a key={item.key} href={item.href} {...props}>
        {label}
      </a>
    )
  }

  /** 產品項：名稱＋一句說明。桌面在彈出層裡，手機直接列在面板上。 */
  const productLink = (item: ProductNavItem, variant: 'desktop' | 'mobile') => {
    const isCurrent = current === item.key
    const props = {
      className:
        variant === 'desktop'
          ? cn(
              'grid gap-px rounded-md px-2.5 py-2 whitespace-normal text-foreground transition-colors hover:bg-fg-soft',
              'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground'
            )
          : 'grid gap-px py-2.5',
      onClick: () => {
        trackSiteLinkClicked(variant === 'desktop' ? 'header_products' : 'mobile_menu', item.href)
        closeMenu()
        closeProducts()
      },
      ...(isCurrent ? { 'aria-current': 'page' as const } : {}),
    }
    const body = (
      <>
        <span
          className={cn(
            'font-medium',
            variant === 'desktop' ? 'text-base leading-normal' : 'text-md',
            isCurrent && 'underline underline-offset-4'
          )}
        >
          {t(item.labelKey)}
        </span>
        <span className="text-xs leading-normal text-muted">{t(item.descKey)}</span>
      </>
    )
    return item.shipped ? (
      <Link key={item.key} href={item.href as Route} {...props}>
        {body}
      </Link>
    ) : (
      <a key={item.key} href={item.href} {...props}>
        {body}
      </a>
    )
  }

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-20 border-b border-border bg-[color-mix(in_oklch,var(--bg)_88%,transparent)] backdrop-blur-[12px]"
    >
      <a
        href="#content"
        className="absolute top-0 -left-[9999px] z-[100] rounded-br-(--radius) bg-foreground px-[18px] py-3 text-background focus:left-0"
      >
        {t('site.nav.skipToContent')}
      </a>
      <div className="mx-auto flex w-full max-w-(--container) items-center justify-between gap-(--gap-md) px-5 py-3.5 min-[561px]:px-(--gutter)">
        <Link
          href="/"
          className="font-display text-xl font-semibold whitespace-nowrap"
          onClick={() => trackSiteLinkClicked('header', '/')}
        >
          <BrandLogo />
        </Link>

        <nav
          aria-label={t('site.nav.primaryNavAriaLabel')}
          className="flex items-center gap-(--gap-lg) text-base max-[900px]:hidden"
        >
          <div
            ref={productsWrapRef}
            className="relative"
            onMouseEnter={() => {
              if (isHoverMode()) openProducts()
            }}
            onMouseLeave={() => {
              if (!isHoverMode()) return
              clearCloseTimer()
              closeTimerRef.current = setTimeout(closeProducts, HOVER_CLOSE_DELAY_MS)
            }}
            onBlur={(event) => {
              const next = event.relatedTarget as Node | null
              if (next && !productsWrapRef.current?.contains(next)) closeProducts()
            }}
          >
            <button
              ref={productsBtnRef}
              type="button"
              aria-expanded={productsOpen}
              aria-controls="site-products-menu"
              // 按鈕不掛 aria-current：它不是連結，沒有「目前頁」這個語意。
              // 目前頁的底線是樣式，語意由彈出層裡那一個 aria-current 承擔。
              className={cn(
                navLinkClass(onProduct, 'inline-flex cursor-pointer items-center gap-1'),
                productsOpen && 'text-foreground'
              )}
              onClick={(event) => {
                // 滑鼠已經 hover 打開了，再點一下不該把它關掉；鍵盤的
                // Enter／Space（detail 0）照常切換。
                if (isHoverMode() && event.detail > 0) {
                  openProducts()
                  return
                }
                if (productsOpen) closeProducts()
                else openProducts()
              }}
            >
              {t('site.nav.products')}
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'size-3.5 transition-transform motion-reduce:[transition:none]',
                  productsOpen && 'rotate-180'
                )}
              />
            </button>
            {/* disclosure，不是 role="menu"——裡面是三個一般連結。
                ::before 是按鈕與彈出層之間那條看不見的橋，滑鼠移過去的途中
                不會離開 wrap 而把選單收掉。
                元素常駐 DOM（`hidden` 是 display:none，轉場沒得播），關閉時
                `visibility` 等轉場播完才收，`inert` 擋掉鍵盤與指標。 */}
            <div
              id="site-products-menu"
              data-state={productsOpen ? 'open' : 'closed'}
              inert={!productsOpen}
              className={cn(
                'absolute top-full -left-3 z-30 mt-3 w-72 rounded-[10px] border border-border bg-background p-[5px] shadow-(--shadow)',
                "before:absolute before:inset-x-0 before:top-[-13px] before:h-[13px] before:content-['']",
                'origin-[24px_0] motion-reduce:[transition:none]',
                productsOpen
                  ? 'visible translate-y-0 scale-100 opacity-100 [transition:opacity_.16s_ease,translate_.2s_cubic-bezier(.2,.7,.2,1),scale_.2s_cubic-bezier(.2,.7,.2,1),visibility_0s_linear_0s]'
                  : 'pointer-events-none invisible -translate-y-1 scale-[.98] opacity-0 [transition:opacity_.16s_ease,translate_.2s_cubic-bezier(.2,.7,.2,1),scale_.2s_cubic-bezier(.2,.7,.2,1),visibility_0s_linear_.2s]'
              )}
            >
              {PRODUCT_ITEMS.map((item) => productLink(item, 'desktop'))}
            </div>
          </div>
          {NAV_ITEMS.map((item) => navLink(item, 'header', ''))}
        </nav>

        <div className="flex items-center gap-(--gap-sm)">
          {authed ? (
            <ButtonLink as={Link} variant="ghost" size="sm" href="/app/account">
              {t('site.nav.account')}
            </ButtonLink>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                closeMenu()
                open()
              }}
            >
              {t('site.nav.signIn')}
            </Button>
          )}
          {/* 漢堡與 ✕ 疊在同一格交叉淡入，所以兩個都常駐 DOM——換掉其中一個
              就沒有可以轉場的對象。格子由 grid-area 疊，不用絕對定位，按鈕
              的尺寸還是由 icon 撐出來。 */}
          <IconButton
            ref={burgerRef}
            type="button"
            className="ml-1 hidden place-items-center max-[900px]:inline-grid"
            aria-label={t('site.nav.menu')}
            aria-expanded={menuOpen}
            aria-controls="site-nav-panel"
            onClick={() => setMenuOpen((isOpen) => !isOpen)}
          >
            <span
              aria-hidden="true"
              className={cn(
                BURGER_ICON_CLASS,
                menuOpen ? 'rotate-45 scale-[.8] opacity-0' : 'rotate-0 scale-100 opacity-100'
              )}
            >
              <Menu />
            </span>
            <span
              aria-hidden="true"
              className={cn(
                BURGER_ICON_CLASS,
                menuOpen ? 'rotate-0 scale-100 opacity-100' : '-rotate-45 scale-[.8] opacity-0'
              )}
            >
              <X />
            </span>
          </IconButton>
        </div>
      </div>

      {/* 面板浮在內容上面，不把整頁往下推：絕對定位在 sticky 頁首的底緣。
          寬度 >900px 一律不顯示，免得從手機寬度拉寬視窗時殘留。 */}
      <div
        id="site-nav-panel"
        data-state={menuOpen ? 'open' : 'closed'}
        inert={!menuOpen}
        className={cn(
          'absolute inset-x-0 top-full z-[25] min-[901px]:hidden',
          // 65px = 實測頁首高度 64.44px 進位：只決定超長面板從哪裡開始捲，
          // 進位讓面板永遠不會超出視窗底緣。
          'max-h-[calc(100dvh-65px)] overflow-y-auto overscroll-contain',
          'border-t border-b border-t-line-soft border-b-border bg-background',
          'shadow-[0_24px_40px_-24px_color-mix(in_oklch,var(--fg)_28%,transparent)]',
          'motion-reduce:[transition:none]',
          menuOpen
            ? 'visible translate-y-0 opacity-100 [transition:opacity_.2s_ease,translate_.26s_cubic-bezier(.2,.7,.2,1),visibility_0s_linear_0s]'
            : 'pointer-events-none invisible -translate-y-2 opacity-0 [transition:opacity_.2s_ease,translate_.26s_cubic-bezier(.2,.7,.2,1),visibility_0s_linear_.26s]'
        )}
      >
        <div className="mx-auto w-full max-w-(--container) px-5 pt-1 pb-3 min-[561px]:px-(--gutter)">
          {/* 手機面板不做第二層折疊：小標＋三項直接攤開，跟桌面同一份文字。 */}
          <p
            id="site-nav-panel-products"
            className="px-0 pt-[14px] pb-0.5 font-mono text-2xs tracking-[0.12em] text-muted"
          >
            {t('site.nav.products')}
          </p>
          <div
            role="group"
            aria-labelledby="site-nav-panel-products"
            className="border-b border-border"
          >
            {PRODUCT_ITEMS.map((item) => productLink(item, 'mobile'))}
          </div>
          {NAV_ITEMS.map((item) =>
            navLink(item, 'mobile_menu', 'block border-b border-line-soft py-3 text-md')
          )}
        </div>
      </div>
    </header>
  )
}
